import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  TextField,
  Box,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';

function Mensaje() {
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [tiempoEntreEscaneos, setTiempoEntreEscaneos] = useState(15); // minutos

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Opciones del combo: cada 15 min hasta 2hs (120 min)
  const scanOptions = [15, 30, 45, 60, 75, 90, 105, 120];

  // Cargamos los datos iniciales desde el backend
  const getMensaje = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      const baseURL = process.env.REACT_APP_SERVER_PATH + 'mensaje';
      const res = await fetch(baseURL);

      if (!res.ok) {
        throw new Error('No se pudo obtener el mensaje inicial');
      }

      const result = await res.json();

      // asumiendo result[0] = { titulo, cuerpo, tiempoEntreEscaneos }
      const data = result[0] || {};

      setTitulo(data.titulo || '');
      setMensaje(data.cuerpo || '');
      if (data.timebetweenscans) {
        setTiempoEntreEscaneos(data.timebetweenscans);
      }
    } catch (err) {
      setErrorMsg(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Guardar cambios
  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const putData = {
        titulo,
        cuerpo: mensaje,
        duracion: tiempoEntreEscaneos, // <-- nuevo campo
      };

      const baseURL = process.env.REACT_APP_SERVER_PATH + 'editmensaje';
      const res = await fetch(baseURL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': 'token-value', // TODO: reemplazar por token real
        },
        body: JSON.stringify(putData),
      });

      if (res.status === 200) {
        setSuccessMsg('Cambios guardados correctamente.');
      } else if (res.status === 401) {
        setErrorMsg('No autorizado. Revisá tus credenciales.');
      } else {
        setErrorMsg('No se pudo guardar. Código ' + res.status);
      }
    } catch (err) {
      setErrorMsg(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    getMensaje();
  }, [getMensaje]);

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 4 }}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        }}
      >
        <CardHeader
          title="Configuración de Mensaje"
          subheader="Editá el mensaje y la frecuencia de escaneo"
          sx={{
            textAlign: 'center',
            pb: 1,
            '& .MuiCardHeader-title': {
              fontWeight: 600,
              fontSize: '1.2rem',
            },
          }}
        />

        <CardContent sx={{ pt: 0 }}>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Cargando datos...
              </Typography>
            </Box>
          )}

          {!loading && errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}

          {!loading && successMsg && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMsg}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            autoComplete="off"
          >
            <Grid container spacing={2}>
              {/* Titulo */}
              <Grid item xs={12}>
                <TextField
                  label="Título"
                  fullWidth
                  required
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  disabled={loading || saving}
                />
              </Grid>

              {/* Cuerpo */}
              <Grid item xs={12}>
                <TextField
                  label="Cuerpo"
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  disabled={loading || saving}
                />
              </Grid>

              {/* tiempoEntreEscaneos */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel id="scan-interval-label">
                    Frecuencia de escaneo (min)
                  </InputLabel>
                  <Select
                    labelId="scan-interval-label"
                    id="scan-interval"
                    label="Frecuencia de escaneo (min)"
                    value={tiempoEntreEscaneos}
                    onChange={(e) =>
                      setTiempoEntreEscaneos(Number(e.target.value))
                    }
                  >
                    {scanOptions.map((minutes) => (
                      <MenuItem key={minutes} value={minutes}>
                        {minutes} min
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  El sistema va a permitir el escaneo cada{' '}
                  <strong>{tiempoEntreEscaneos} minutos</strong>.
                </Typography>
              </Grid>
            </Grid>

            <CardActions
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="contained"
                type="submit"
                disabled={saving || loading}
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Guardando...
                  </>
                ) : (
                  'Guardar cambios'
                )}
              </Button>
            </CardActions>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default Mensaje;
